import React, { FC, useEffect } from 'react';
import {
  DataGridLayout,
  Filters,
  FiltersLayout,
  List,
  ListHeader,
  ListPageLayout,
  useListContext,
} from 'framework/list';
import { TextField } from 'framework/ui/field';
import EmployeeSpecializationFilter from '../../List/filter/EmployeeSpecializationFilter';
import { Box, Typography } from '@mui/material';
import CompetencyLevelFilter from 'domain/people/employee/performanceMeasurement/List/filter/CompetencyLevelFilter';
import PerformanceMeasurementOfficeFilter from 'domain/people/employee/performanceMeasurement/List/filter/PerformanceMeasurementOfficeFilter';
import { Tooltip } from 'framework/ui/mui-builder';
import InfoIcon from '@mui/icons-material/InfoOutlined';
import {
  EmployeeCompetencyLevelField,
  EmployeeLinkFieldWithNameLoading,
  EmployeeOfficeField,
} from 'domain/shared/employee';
import { usePerformanceMeasurementService, CalculatePercentileModalButton } from 'domain/shared/performanceMeasurement';
import PerformanceMeasurementPercentileField from 'domain/people/employee/performanceMeasurement/shared/field/PerformanceMeasurementPercentileField';
import EmployeePositionWithSpecializationField from 'domain/people/employee/performanceMeasurement/shared/field/EmployeePositionWithSpecializationField';
import { Show, useShowContext } from 'framework/show';

const CalculatePercentileButtonWithFilter: React.FC = () => {
  const { filter } = useListContext();
  const initialPosition = filter?.position?.in?.[0] ?? null;
  return <CalculatePercentileModalButton initialPosition={initialPosition} />;
};

const RankingFilters: FC = () => {
  const { data, setData } = useShowContext();

  const { getQualificationAndLocations, getPositionAndSpecialization } = usePerformanceMeasurementService();

  const { filter } = useListContext();

  const position = filter?.position ? filter.position?.in?.[0] : null;
  const { positionId, specializationId } = getPositionAndSpecialization(position);

  useEffect(() => {
    if (positionId) {
      (async () => {
        const qualificationAndLocations = await getQualificationAndLocations({
          positionId,
          technologyId: specializationId,
        });
        setData &&
          setData({
            ...data,
            ...qualificationAndLocations,
          });
      })();
    } else {
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [positionId, specializationId]);

  if (!data.availableQualificationToLocation) return null;
  return (
    <Box
      sx={{
        '.filters-reset-btn': {
          display: 'none',
        },
      }}
    >
      <Filters>
        <FiltersLayout>
          <EmployeeSpecializationFilter
            source="position"
            label="Specialization"
            labelInDropdown="Specialization"
            technologyProps={{
              source: 'technology',
              label: 'Technology',
              labelInDropdown: 'technology',
            }}
          />
          <CompetencyLevelFilter
            source="competencyLevel"
            label="Level"
            locationFilterSource="officeId"
            positionSource="position"
          />
          <PerformanceMeasurementOfficeFilter
            source="officeId"
            label="Location"
            competencyLevelSource="competencyLevel"
            positionSource="position"
          />
        </FiltersLayout>
      </Filters>
    </Box>
  );
};

const PerformanceMeasurementPageLayout = () => {
  const { retrievePerformanceRank, getPositionsWithTechnologies } = usePerformanceMeasurementService();

  const prepareLabel = (title: string, tooltipTitle: JSX.Element) => {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, alignItems: 'center' }}>
        <Typography variant="body2">{title}</Typography>
        <Tooltip title={tooltipTitle}>
          <InfoIcon sx={{ color: 'text.secondary', width: 16 }} />
        </Tooltip>
      </Box>
    );
  };

  return (
    <List
      getDataMethod={retrievePerformanceRank}
      defaultSortOrder={undefined}
      defaultFilter={{
        position: { in: ['47--JAVA'] },
        officeId: { in: [2] },
      }}
    >
      <ListPageLayout
        header={
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <ListHeader label="Performance Measurement and Ranking" />
            <CalculatePercentileButtonWithFilter />
          </Box>
        }
        filter={
          <Show getDataMethod={getPositionsWithTechnologies}>
            <RankingFilters />
          </Show>
        }
      >
        <DataGridLayout showTotal={false}>
          <TextField source="index" label="№" width="10%" />
          <EmployeeLinkFieldWithNameLoading source="employeeAlias" label="Worker" width="15%" />
          <EmployeePositionWithSpecializationField
            label="Position"
            positionIdSource="positionId"
            specializationIdSource="specialization"
            width="15%"
          />
          <EmployeeCompetencyLevelField source="competencyLevel" label="Competency level" width="15%" />
          <EmployeeOfficeField label="Location" source="officeId" width="15%" />
          <TextField
            source="score"
            label={prepareLabel(
              'Score',
              <ul>
                <p>
                  Worker Score represents a normalized performance metric (0.0–1.0) calculated from various weighted
                  factors.
                </p>
              </ul>,
            )}
            width="15%"
          />
          <PerformanceMeasurementPercentileField
            source="percentile"
            label={prepareLabel(
              '%',
              <ul>
                <p>Percentile is calculating based on salary within level, specialization and location.</p>
                <p>
                  A lower percentile means better performance for the same or lower salary, thus increasing the ranking.
                </p>
              </ul>,
            )}
            width="15%"
          />
        </DataGridLayout>
      </ListPageLayout>
    </List>
  );
};

export default PerformanceMeasurementPageLayout;
